import { useCallback, useEffect, useMemo, useState } from "react";
import { ICrewEvent, ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { useDispatch, useSelector } from "react-redux";
import {
  IDisplayPairing,
  IPairingPosition,
  IPairingLogicModifier,
  IPosition,
  IDisplayBidPairing,
  ClientSettingName,
  //IDisplayBidNFA,
} from "@vmosolutions/oc-interfaces";
import { closeContextMenu, openContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { openHistoryWindow, openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import { ISummaryItem } from "../../FlightSummaryList/SummaryList";
import CrewScheduleEventSummaryList, { ISummarySubHeader } from "../CrewScheduleEventSummaryList";
// import moment from "moment";
import {
  CardContent,
  MenuItem,
  Typography,
  Grid,
  Chip,
  Tooltip,
  Theme,
  AlertTitle,
  Alert,
  Paper,
  Box,
} from "@mui/material";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { removePairingFromCrew } from "src/redux/actions/CrewSchedule/pairings";
import {
  convertToPlannedPairing,
  deletePlannedEvent,
  setLastOpenFlyingAssignmentResponse,
  setSelectedCrewEvents,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { EventModalTypes } from "../../DialogModal/DialogModalWrapper";
// Pairing Display components
import { ShortCodeChip } from "../../ShortCodes/ShortCodeChip";
import { IHoveredEvent } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { formatDateString } from "src/components/DateDisplay";
import { defaultTimezone } from "src/redux/reducers/CrewScheduleReducer";
import { ShortDescription } from "src/components/Pairings/ShortDescription";
import { IModalInfo } from "src/pages/CrewSchedule/CrewScheduleWindow";
import { updateCrewBidEvent } from "src/redux/actions/Bidding.Actions";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import _ from "lodash";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import { getClientSetting, openSnackbar } from "src/redux/actions/App.Actions";
import chroma from "chroma-js";
import { PositionBadge } from "../PositionBadge";
import { PairingErrorTypes } from "src/components/DialogModal/PairingContextMenuModals/PairingErrorModal";

interface IPairingContextProps {
  id: string;
  path: string[];
  selectedPairingEvents: ICrewEvent[];
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
}

export const PairingContext = (props: IPairingContextProps): JSX.Element => {
  const { id, path, selectedPairingEvents, setActiveEventModal } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [descSetting, setDescSetting] = useState<boolean>(true);
  const dispatch = useDispatch();

  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );

  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;
  const { timezoneId } = displayTimezone;

  const multiSelect: boolean = selectedPairingEvents?.length > 1;
  const isPlanned: boolean = selectedPairingEvents?.[0]?.isPlanned;

  // Bidding Parameters
  const isBidPairing = !!(selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.bidPairingId;
  const pointingUpGreen =
    (isBidPairing &&
      !(selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.assigned &&
      !(selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.awarded) ||
    ((selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.assigned &&
      (selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.awarded);
  const assignedBidPairing = isBidPairing && (selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.assigned;
  const awardedBidPairing = isBidPairing && (selectedPairingEvents?.[0]?.event as IDisplayBidPairing)?.awarded;

  // Selected pairing is an open flying request rendered in ghost form on crew members row
  const openFlyingRequest = (selectedPairingEvents?.[0]?.event as any)?.openFlyingRequest;

  const pairing = useMemo(() => {
    const pairingEvent: ICrewEvent | null = selectedPairingEvents.length > 0 ? selectedPairingEvents[0] : null;
    return pairingEvent?.event as IDisplayPairing | IDisplayBidPairing;
  }, [selectedPairingEvents]);

  const crewMemberSelected: ICrewMember | undefined = useMemo(() => {
    const pairingEvent: ICrewEvent | null = selectedPairingEvents.length > 0 ? selectedPairingEvents[0] : null;
    return crew.find((members: ICrewMember) => members._id === pairingEvent?.crewId);
  }, [selectedPairingEvents, crew]);

  // If selected crew member is assigned to this pairing as a crew, return his info, otherwise returns null
  const crewMemberOnSelectedPairing: ICrewMember | null = useMemo(() => {
    const pairingEvent: ICrewEvent | null = selectedPairingEvents.length > 0 ? selectedPairingEvents[0] : null;
    if (!pairingEvent || !crewMemberSelected) {
      return null;
    }
    const pairingWithCrewMember = (pairingEvent?.event as IDisplayPairing)?.positions?.find(
      (pos: any) => pos.crew_id === crewMemberSelected?._id
    );
    return pairingWithCrewMember ? crewMemberSelected : null;
  }, [selectedPairingEvents, crewMemberSelected]);

  useEffect(() => {
    const getPairingDescriptionSetting = async (): Promise<void> => {
      const descriptionSetting = await getClientSetting(ClientSettingName.PairingDescription);
      setDescSetting(descriptionSetting?.setValue?.enabled ?? true);
    };
    getPairingDescriptionSetting();
  }, []);

  const getPositionDisplayOrder = useCallback(
    (positionTitle: string): number => {
      return positions.find((position: IPosition) => position.title === positionTitle)?.displayOrder ?? Infinity;
    },
    [positions]
  );
  const summaryItems: Array<ISummaryItem | ISummarySubHeader> = useMemo(
    () =>
      pairing
        ? [
            {
              key: `${pairing._id}-shortDescription`,
              value: <ShortDescription description={pairing.shortDescription} />,
              label: "Description",
            },
            {
              key: `${pairing._id}-icao`,
              value: pairing.baseICAO ?? "Unspecified",
              label: "Base",
            },
            {
              key: "pairingStart",
              value: formatDateString(pairing.start, timezoneId),
              label: "Start",
            },
            {
              key: "pairingEnd",
              value: formatDateString(pairing.end, timezoneId),
              label: "End",
            },
            ...pairing.positions
              .map((position: IPairingPosition) => {
                const crewMember = crew.find((value: ICrewMember) => position.crew_id === value._id);
                // ${crewMember?.crewInfo?._old_id ? "[" + crewMember.crewInfo._old_id + "]" : ""}
                const posBidOn =
                  isBidPairing &&
                  (pairing as IDisplayBidPairing)?.positionBidOn === position.position &&
                  !position?.crew_id
                    ? "(Bid On)"
                    : "";
                const awardedTo =
                  isBidPairing &&
                  (pairing as IDisplayBidPairing)?.awarded &&
                  position?.crew_id === crewMemberOnSelectedPairing?._id
                    ? "(Awarded)"
                    : "";
                return {
                  key: `${position.position}-${position.crew_id}`,
                  value: crewMember?.crewInfo ? `${crewMember.lastName}, ${crewMember.firstName}` : "Unassigned",
                  label: (
                    <Box sx={{ display: "inline-block" }}>
                      <PositionBadge
                        key={`${position.position}-${position.crew_id}`}
                        pairingPosition={position}
                        filled={position.crew_id ? true : false}
                      />
                      {`${posBidOn} ${awardedTo}`}
                    </Box>
                  ),
                  masterSeniority: crewMember?.crewInfo?.masterSeniority ?? Infinity,
                  displayOrder: getPositionDisplayOrder(position.position),
                };
              })
              .sort(
                (a, b) =>
                  a.displayOrder - b.displayOrder || (a.masterSeniority ?? Infinity) - (b.masterSeniority ?? Infinity)
              ),
          ]
        : null,
    [pairing, timezoneId, isBidPairing, crew, crewMemberOnSelectedPairing?._id, getPositionDisplayOrder]
  );

  const editPairing = (): void => {
    const editorWidth = 925;
    // open menu to edit selected pairing
    dispatch(closeContextMenu(id));
    // showSnackbar("Edit Pairing Action");
    dispatch(
      openWindow(
        "Edit Pairing",
        "PairingEditorWindow",
        path,
        { pairingId: pairing._id },
        "context",
        true,
        MosaicDirection.Right,
        editorWidth
      )
    );
  };

  const openPairingSummary = useCallback((): void => {
    // close context menu
    const crewId = selectedPairingEvents[0].crewId;
    const itemID = isBidPairing ? (pairing as IDisplayBidPairing).bidPairingId : pairing._id;
    dispatch(closeContextMenu(id));
    dispatch(
      openWindow(
        `${pairing.shortId} Summary`,
        "PairingSummaryWindow",
        path,
        { pairingId: itemID, crewId, isBidPairing },
        "singleContext",
        true
      )
    );
  }, [dispatch, id, isBidPairing, pairing, path, selectedPairingEvents]);

  /** Function to toggle bid assignment status of bidPairing. */
  const toggleAssignBidPairing = useCallback(() => {
    if (!isBidPairing) {
      return;
    }
    const bidPairingEvent = { ...selectedPairingEvents[0] };
    const updatedBidPairing: IDisplayBidPairing = {
      ...(bidPairingEvent.event as IDisplayBidPairing),
      assigned: !(bidPairingEvent.event as IDisplayBidPairing).assigned,
    };
    const updatedCrewEvent: ICrewEvent = { ...bidPairingEvent, event: updatedBidPairing };
    dispatch(updateCrewBidEvent(updatedCrewEvent, "pairing"));
    dispatch(setSelectedCrewEvents([updatedCrewEvent], false, false, false));
  }, [dispatch, isBidPairing, selectedPairingEvents]);

  const convertToPlanned = async (): Promise<void> => {
    setIsLoading(true);
    const plannedPairing = await convertToPlannedPairing(pairing._id, [crewMemberOnSelectedPairing?._id]);
    dispatch(closeContextMenu(id)); // currently the context menu is blank and useless for planned pairings.
    dispatch(
      setSelectedCrewEvents(
        [{ event: plannedPairing, crewId: crewMemberOnSelectedPairing?._id, isPlanned: true }],
        false,
        false,
        false
      )
    );
    setIsLoading(false);
  };

  /**
   * Assign this open flying request to selected crew member.
   */
  const assignOpenFlightRequest = useCallback(async (): Promise<void> => {
    if (!crewMemberSelected || !openFlyingRequest) {
      return;
    }
    const crewMemberName = `${crewMemberSelected.firstName.substring(0, 1)}. ${crewMemberSelected.lastName}`;
    const requestId = openFlyingRequest?.find((request: any) => request?.user_id === crewMemberSelected?._id)?._id;
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `Assigning Open Flying Request to ${crewMemberName}`,
      errorMessage: `Error: Unable to Assign Open Flying Request to ${crewMemberName}`,
    };
    const response = await AmplifyAPIWrapper.Put<any>(`/pairings/openflying/assign/${requestId}`, null, notifications);
    if (response.errors && response.errors.length > 0) {
      //Alert user!!
      setActiveEventModal({
        type: EventModalTypes.PairingError,
        modalProps: {
          errors: response.errors,
          title: "Pairing Not Assigned",
          pairingId: pairing._id,
          pairingShortCode: pairing.shortId,
          crewMember: crewMemberSelected,
          pairingErrorTypes: PairingErrorTypes.OpeningFlyingAssignmentError,
          openFlyingRequest: requestId,
        },
      });
    } else if (response?.crewRequests?.[0]?.actionType === "awarded") {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: `Open Flying Request Successfully Assigned to ${crewMemberName}`,
        })
      );
      // If successfully store response and date so that open flight window can be refreshed.
      dispatch(setLastOpenFlyingAssignmentResponse(new Date(), response?.crewRequests?.[0]));
      // Update the selected pairing so that assignment link becomes unassign link.
      const updatedPairing: ICrewEvent = { ...selectedPairingEvents[0] };

      if (response?.pairing?._id === updatedPairing?.event?._id) {
        dispatch(
          setSelectedCrewEvents(
            [_.merge(updatedPairing, { ...response?.pairing, openFlyingRequest: null })],
            false,
            false,
            false
          )
        );
        dispatch(openContextMenu(`${id}`));
      } else {
        dispatch(setSelectedCrewEvents([], false, false, false));
      }
    }
  }, [crewMemberSelected, openFlyingRequest, dispatch, selectedPairingEvents, id]);

  const unassignPairing = async (): Promise<void> => {
    // remove pairing
    setIsLoading(true);
    await removePairingFromCrew(pairing._id, crewMemberOnSelectedPairing?._id);
    dispatch(closeContextMenu(id));
    dispatch(setSelectedCrewEvents([], false, false, false));
    setIsLoading(false);
  };

  const openSplitPairing = (): void => {
    dispatch(closeContextMenu(id));
    if (setActiveEventModal) setActiveEventModal({ type: EventModalTypes.SplitPairing });
  };

  const handleDeletePairing = (): void => {
    dispatch(closeContextMenu(id));
    if (setActiveEventModal) setActiveEventModal({ type: EventModalTypes.DeletePairing });
  };

  const handleRemovePlannedEvent = (hoveredEvent: IHoveredEvent) => async (): Promise<void> => {
    setIsLoading(true);
    await deletePlannedEvent(hoveredEvent);
    dispatch(closeContextMenu(id));
    dispatch(setSelectedCrewEvents([], false, false, false));
    setIsLoading(false);
  };
  const openHistory = (): void => {
    dispatch(
      openHistoryWindow(
        "Pairing History",
        {
          collection: "pairings",
          id: pairing._id,
          pairing: pairing,
        },
        path
      )
    );
    dispatch(closeContextMenu(id));
  };

  /** Display information on logic Modifiers if applied to this pairing. */
  const pairingLogicModifiers = (logicModifiers: IPairingLogicModifier[]): JSX.Element | false => {
    return (
      logicModifiers?.length > 0 && (
        <Grid container>
          <Grid
            item
            xs={12}
            sx={(theme) => ({
              backgroundColor: chroma(theme.palette.background.default).darken(0.5).css(),
              p: 0.5,
              pl: 2,
              borderBottom: `1px solid  ${chroma(theme.palette.background.default).brighten(0.5).css()}`,
            })}
          >
            Logic Modifiers
          </Grid>
          <Grid item container spacing={0} sx={{ justifyContent: "center", p: 1 }}>
            {logicModifiers?.map((lm: IPairingLogicModifier, index: number) => {
              return (
                <Tooltip
                  key={`lm_${lm?._id}_index_${index}`}
                  enterDelay={250}
                  placement="top"
                  title={lm.logicModifier.description}
                >
                  <Chip
                    label={`${lm.logicModifier.title} [${lm.dutyTime ? "DUTY" : "PAIRING"}]`}
                    sx={{
                      borderRadius: "3px",
                      fontWeight: 900,
                      margin: "3px",
                      fontSize: "0.8em",
                      backgroundColor: `#${lm.logicModifier.color}`,
                      color: invertHexColor(lm.logicModifier.color),
                    }}
                    size="small"
                  />
                </Tooltip>
              );
            })}
          </Grid>
        </Grid>
      )
    );
  };

  const setSelectedPairing = (selectedPairing: ICrewEvent) => () => {
    dispatch(setSelectedCrewEvents([selectedPairing], false, false, true));
  };

  if (!pairing) {
    return <></>;
  }

  return (
    <>
      {selectedPairingEvents.length === 1 && (
        <Grid container direction="column">
          <CrewScheduleEventSummaryList
            key={`${props.id}-${pairing._id}-summaryList`}
            items={summaryItems}
            hideTitle={true}
          />
        </Grid>
      )}

      {selectedPairingEvents.length === 1 &&
        pairing?.logicModifiers?.length > 0 &&
        pairingLogicModifiers(pairing.logicModifiers)}

      <Grid
        item
        xs={12}
        sx={(theme) => ({
          backgroundColor: chroma(theme.palette.background.default).darken(0.5).css(),
          p: 0.5,
          pl: 2,
          borderBottom: `1px solid  ${chroma(theme.palette.background.default).brighten(0.5).css()}`,
        })}
      >
        Actions
      </Grid>
      {!multiSelect && !isPlanned && (
        <CardContent>
          {!isBidPairing ? (
            <>
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${pairing._id}-contextMenu-editPairingBtn`}
                disabled={isLoading}
                onClick={editPairing}
              >
                Edit Pairing <ShortCodeChip code={pairing.shortId} itemId={pairing._id} type="pairing" />
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${pairing._id}-contextMenu-pairingSummaryBtn`}
                disabled={isLoading}
                onClick={openPairingSummary}
              >
                Pairing Summary
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${pairing._id}-contextMenu-convertToPlannedBtn`}
                disabled={isLoading}
                onClick={convertToPlanned}
              >
                Convert To Planned
              </MenuItem>
              {crewMemberOnSelectedPairing?._id && (
                <MenuItem
                  sx={{ fontSize: "0.9em" }}
                  key={`${props.id}-${pairing._id}-contextMenu-removePairingBtn`}
                  disabled={isLoading}
                  onClick={unassignPairing}
                >
                  {`Remove ${crewMemberOnSelectedPairing.firstName.substring(0, 1)}. ${
                    crewMemberOnSelectedPairing.lastName
                  } from Pairing`}
                </MenuItem>
              )}
              {openFlyingRequest && crewMemberSelected && (
                <MenuItem
                  sx={{ fontSize: "0.9em" }}
                  key={`${props.id}-${pairing._id}-contextMenu-assignOpenFlightBtn`}
                  disabled={isLoading}
                  onClick={_.debounce(assignOpenFlightRequest, 500)}
                >
                  {`Assign Open Flying Request To ${crewMemberSelected.firstName.substring(0, 1)}. ${
                    crewMemberSelected.lastName
                  }`}
                </MenuItem>
              )}
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${pairing._id}-contextMenu-splitPairingBtn`}
                disabled={isLoading}
                onClick={openSplitPairing}
              >
                Split Pairing
              </MenuItem>
              <MenuItem sx={{ fontSize: "0.9em" }} disabled={isLoading} onClick={openHistory}>
                View Pairing History
              </MenuItem>
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${pairing._id}-contextMenu-deletePairingBtn`}
                disabled={isLoading}
                onClick={handleDeletePairing}
              >
                Delete Pairing
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${(pairing as IDisplayBidPairing).bidPairingId}-contextMenu-bidpairingSummaryBtn`}
                disabled={isLoading}
                onClick={openPairingSummary}
              >
                Bid Pairing Summary (snapshot)
              </MenuItem>
              {
                <MenuItem
                  key={`${props.id}-${
                    (pairing as IDisplayBidPairing).bidPairingId
                  }-contextMenu-bidpairingToggleAssignBtn`}
                  disabled={isLoading}
                  onClick={toggleAssignBidPairing}
                  sx={(theme) => ({
                    color: pointingUpGreen ? theme.custom.forms.dirtyColor : theme.palette.error.main,
                    fontSize: "0.9em",
                  })}
                >
                  <DoubleArrowIcon
                    style={{
                      color: "inherit",
                      transform: pointingUpGreen ? "rotate(-90deg)" : "rotate(90deg)",
                      marginRight: 5,
                      width: 14,
                    }}
                  />
                  {awardedBidPairing && assignedBidPairing
                    ? "Unstage change to Pairing Bid"
                    : awardedBidPairing && !assignedBidPairing
                    ? "Stage unaward of Pairing Bid"
                    : !awardedBidPairing && assignedBidPairing
                    ? "Unstage change to Bid Pairing"
                    : "Stage Award of Pairing Bid"}
                </MenuItem>
              }
            </>
          )}
        </CardContent>
      )}

      {selectedPairingEvents.length > 1 && (
        <CardContent>
          <Paper
            elevation={4}
            sx={(theme) => ({
              my: "4px !important",
              mx: 0,
              border: `1px solid ${theme.palette.warning.light}`,
              borderRadius: "4px",
              p: 0.75,
              backgroundColor: chroma(theme.palette.warning.dark).darken(1).desaturate(1).css(),
              fontSize: "0.8em",
              width: "100%",
            })}
          >
            Overlapping {isBidPairing ? "bid pairings" : "pairings"}
            <br />
            Please choose 1 from below to continue
          </Paper>

          {selectedPairingEvents.map((selectedPairingEvent) => {
            const selectedPairing = selectedPairingEvent.event as IDisplayPairing | IDisplayBidPairing;
            return (
              <MenuItem
                sx={{ fontSize: "0.9em" }}
                key={`${props.id}-${selectedPairing._id}-contextMenu`}
                disabled={isLoading}
                onClick={setSelectedPairing(selectedPairingEvent)}
              >
                <>
                  <Typography variant="caption" color="textPrimary" style={{ marginRight: "15px" }}>
                    {selectedPairing?.shortId}
                  </Typography>
                  {isBidPairing && (
                    <Typography variant="caption" color="textPrimary" style={{ marginRight: 15 }}>
                      Position Bid On: {(selectedPairing as IDisplayBidPairing)?.positionBidOn}
                    </Typography>
                  )}
                  <ShortDescription description={selectedPairing?.shortDescription} />{" "}
                </>
              </MenuItem>
            );
          })}
        </CardContent>
      )}
      {!multiSelect && isPlanned && (
        <CardContent>
          <MenuItem
            sx={{ fontSize: "0.9em" }}
            key={`${props.id}-${pairing._id}-contextMenu-deletePairingBtn`}
            disabled={isLoading}
            onClick={handleRemovePlannedEvent(selectedPairingEvents?.[0])}
          >
            Remove Planned Event
          </MenuItem>
        </CardContent>
      )}
    </>
  );
};
